/**
 * AgentBadge — pill discret indiquant qu'une row a été créée/résolue par
 * un agent IA / système externe (D30 forensic visible — Chunk 9.M.7).
 *
 * Variants :
 *   - `created`   → microcopy « Créé par agent IA {name} » / « Created by AI agent {name} »
 *   - `fulfilled` → microcopy « Résolu par agent IA {name} » / « Resolved by AI agent {name} »
 *
 * Affichage conditionnel :
 *   - Si `displayName` est null/undefined/empty → composant retourne null
 *     (pas de fallback texte cassé, pas de badge vide).
 *   - Si agent disabled mais jointure DB toujours active → badge visible avec
 *     display_name historique (forensic preservation, ADR 0021 §2.4).
 *   - Si agent supprimé (jointure renvoie null) → badge caché proprement.
 *
 * Style :
 *   - Pill discret violet 50/300/700 (parité brand accent — distinct des
 *     status badges colorés métier emerald/amber/red).
 *   - Icône robot inline SVG (zéro dépendance externe).
 *   - Tooltip `title` natif explicite (ADR 0022 forensic notation).
 */

import { useTranslation } from "react-i18next";

export type AgentBadgeVariant = "created" | "fulfilled";

type Props = {
  /** display_name de l'agent. Si null/undefined/empty → composant retourne null. */
  readonly displayName: string | null | undefined;
  /** Variant microcopy : created (créateur de la row) vs fulfilled (résolveur). */
  readonly variant?: AgentBadgeVariant;
};

function IconAgentInline({ className }: { className?: string }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect
        x="3"
        y="4.5"
        width="10"
        height="7.5"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <path
        d="M8 2v2.5M6 7.5h.01M10 7.5h.01M6.5 10h3"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function AgentBadge({ displayName, variant = "created" }: Props) {
  const { t } = useTranslation();
  const trimmed = (displayName ?? "").trim();
  if (trimmed.length === 0) return null;

  const labelKey =
    variant === "fulfilled" ? "agent_badge.fulfilled_by" : "agent_badge.created_by";
  const label = t(labelKey, { name: trimmed });
  const tooltip = t("agent_badge.tooltip");

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-violet-200"
      title={tooltip}
    >
      <IconAgentInline className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </span>
  );
}
